import { FC, useMemo, useState } from "react";

import { Icon } from "@/components";
import {
    Checkbox,
    IconButton,
    ProductSearchAutocomplete,
    type ProductSearchOption,
} from "@/ui";
import { LineItem, Product } from "@/types";
import { mapProductToLineItem } from "@/utils";

import { useSearchProducts } from "./hooks";
import styles from "./AddLineItemForm.module.scss";

interface Props {
    lineItems: LineItem[];
    addLineItem: (item: LineItem) => void;
    hideForm: () => void;
    providerStoreID?: string;
}

const CloseIcon = () => <Icon icon="close" />;

const AddLineItemForm: FC<Props> = ({
    lineItems,
    addLineItem,
    hideForm,
    providerStoreID,
}) => {
    const [filter, setFilter] = useState("");
    const [outOfStock, setOutOfStock] = useState(false);
    const { products, isSearching, statusMessage, isSearchEnabled } =
        useSearchProducts({
            filter,
            outOfStock,
            providerStoreID,
        });
    const lineItemIds = useMemo(
        () => lineItems?.map(({ id }) => id),
        [lineItems],
    );

    const availableProducts = useMemo(
        () => products.filter(({ id }) => !lineItemIds.includes(id)),
        [products, lineItemIds],
    );

    const options: ProductSearchOption[] = useMemo(
        () =>
            availableProducts.map(product => ({
                value: String(product.id ?? ""),
                label: product.name ?? "",
                imageUrl: product.asset?.image ?? undefined,
            })),
        [availableProducts],
    );

    const handleFilterChange = (nextFilter: string) => {
        setFilter(nextFilter);
    };

    const handleCheckboxChange = (checked: boolean) => {
        setOutOfStock(checked);
    };

    const handleAddLineItem = (product: Product) => {
        hideForm();
        addLineItem({ ...mapProductToLineItem(product), quantity: 1 });
    };

    const handleProductSelect = (productId: string | null) => {
        if (!productId) return;
        const product = availableProducts.find(
            item => String(item.id) === productId,
        );
        if (!product) return;
        handleAddLineItem(product);
    };

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <IconButton
                    aria-label="Close add product"
                    onClick={hideForm}
                    size="sm"
                    type="button"
                    variant="tertiary"
                >
                    <CloseIcon />
                </IconButton>
                <p className={styles.title}>Add product</p>
            </div>

            <div className={styles.body}>
                <div className={styles.includeField}>
                    <p className={styles.fieldLabel}>Include</p>
                    <Checkbox
                        checked={outOfStock}
                        disabled={!isSearchEnabled}
                        label="Out-of-stock items"
                        onCheckedChange={handleCheckboxChange}
                    />
                </div>

                <div className={styles.productField}>
                    {statusMessage ? (
                        <p className={styles.status} role="status">
                            {statusMessage}
                        </p>
                    ) : null}
                    <ProductSearchAutocomplete
                        disabled={!isSearchEnabled}
                        loading={isSearching}
                        onChange={handleProductSelect}
                        onQueryChange={handleFilterChange}
                        options={options}
                        placeholder="Search for a product"
                    />
                </div>
            </div>
        </div>
    );
};

export { AddLineItemForm };
